import type { DpiVerdict } from "../../src/image/dpi.ts";
import { CONTACT_LIMITS, phoneOk } from "../../src/lib/contact.ts";
import { EMAIL_RE } from "../../src/lib/email-address.ts";
import { printTierLabel } from "../../src/lib/estimate.ts";
import type { PrintTierPair } from "../../src/lib/estimate.ts";
import { money } from "../../src/lib/money.ts";
import type { Offset } from "../../src/lib/offset.ts";
import { tierForBox } from "../../src/lib/pricing.ts";
import type { Spot } from "../../src/lib/types.ts";
import { effectiveDpi, fitPrintSize } from "../../src/image/dpi.ts";
import { gangRatioOk, TRANSFER_LIMITS } from "../../src/lib/transfers.ts";
import type { TransferKind } from "../../src/lib/transfers.ts";
import { cleanQuantities, sliderBounds } from "./cart.ts";
import type { Discount } from "./discount.ts";
import type { Ink, StudioGarment } from "./studio.ts";
import { OWN_COLORS, OWN_ID, OWN_LABEL, OWN_PPI, OWN_SIZES, OWN_SPOTS, ownColor, ownSides, silhouetteSvg } from "../../src/lib/own-garments.ts";
import type { OwnGarment } from "../../src/lib/own-garments.ts";

// Finished prices as the server sends them (designer-decisions.md section D): never a base price or a percentage.
export interface Dual {
  bankCents: number;
  cardCents: number;
}

// One color as the designer's catalog sends it: the Studio's garment plus its priced sizes. Its prices depend on its
// prints, so the size table shows the estimate's.
export interface DesignGarment extends StudioGarment {
  sizes: string[];
}

// One print tier as the catalog sends it: its name, its box and its two prices at list (never the base).
export interface CatalogTier extends Dual {
  name: string;
  maxWidthIn: number;
  maxHeightIn: number;
}

// The server's answer to `estimate` for what is entered: every picked color's unit prices with its prints, the print
// tier lines, and, once there are garments, the totals.
export interface Estimate {
  garmentCount: number;
  // Null while no color has a garment yet.
  total: Dual | null;
  units: Record<string, Record<string, Dual>>;
  addForBreak: number | null;
  // The quantity tier reached and what it saves, or null below the first one.
  discount: Discount | null;
  // Blank shipping inside the totals as the two prices (0 and 0 once waived) and the goods total that makes it free;
  // null without totals or for the buyer's own garments.
  blankShipping?: { bankCents: number; cardCents: number; freeAtCents: number } | null;
  // "Front center, medium" with its two prices, one per placement.
  prints: PrintTierPair[];
}

// One placement as the page has sized it (the server sizes it the same way).
export interface SizedPlacement {
  spot: Spot;
  widthIn: number;
  heightIn: number;
}

export type Quantities = Record<string, Record<string, number>>;

export interface Contact {
  name: string;
  email: string;
  phone: string;
  business: string;
  note: string;
}

export type ContactErrors = Partial<Record<keyof Contact, string>>;

// The checks the server makes (src/api/design-input.ts and cleanNote), so the buyer hears about them before Send.
export function contactErrors(f: Contact): ContactErrors {
  const e: ContactErrors = {};
  const name = f.name.trim();
  const email = f.email.trim();
  if (!name || name.length > CONTACT_LIMITS.name) e.name = "Enter your name.";
  if (!EMAIL_RE.test(email) || email.length > CONTACT_LIMITS.email) e.email = "Enter a valid email address.";
  if (!phoneOk(f.phone)) e.phone = "Enter a phone number with digits, or leave it blank.";
  if (f.business.trim().length > CONTACT_LIMITS.business) e.business = "Keep the business name to 200 characters or fewer.";
  if (f.note.trim().length > CONTACT_LIMITS.note) e.note = "Keep the note to 500 characters or fewer.";
  return e;
}

// The two lines under a size; blank until the estimate prices it.
export function unitLines(d: Dual | null): [string, string] {
  return d ? [`Bank ${money(d.bankCents)}`, `Card ${money(d.cardCents)}`] : ["", ""];
}

// Buyer words for the DPI note. Nothing prints from this page, so soft art is a warning here, never a block.
export function designDpiNotice(dpi: number, verdict: DpiVerdict): { cls: "good" | "warn"; text: string } {
  if (verdict === "ok") return { cls: "good", text: `Sharp at this size (${dpi} DPI).` };
  if (verdict === "warn") return { cls: "warn", text: `This will print a little soft at this size (${dpi} DPI). A larger file would be sharper.` };
  return { cls: "warn", text: `This will print soft at this size (${dpi} DPI). We will ask for a larger file before anything prints.` };
}

// Whole garments of one color entered so far.
export function colorCount(q?: Record<string, number>): number {
  return Object.values(q ?? {}).reduce((n, v) => n + (Number.isFinite(v) && v > 0 ? Math.floor(v) : 0), 0);
}

// Picked colors with no garments yet, by color name (Send waits until each has one).
export function colorsMissingQuantities(garments: DesignGarment[], q: Quantities): string[] {
  return garments.filter((g) => colorCount(q[g.id]) === 0).map((g) => g.color);
}

// The estimate request for what is picked, or null until it can be priced: a price needs the colors, the artwork and
// at least one placement (the prints are part of every garment's price). Colors still empty go along with no
// quantities, so the size table shows their prices before anything is entered.
// The buyer's own garments send `own` and no garment ids (the server prices their prints only).
export function estimateBody(
  garments: DesignGarment[],
  q: Quantities,
  art: { logoId: string; placements: ({ spot: Spot; widthIn: number } & Partial<Offset>)[] } | null,
  own: OwnGarment | null = null,
): Record<string, unknown> | null {
  if (garments.length === 0 || !art || art.placements.length === 0) return null;
  return {
    action: "estimate", ...(own ? { product: "own", own } : { garmentIds: garments.map((g) => g.id) }), logoId: art.logoId, placements: art.placements,
    quantities: Object.fromEntries(garments.map((g) => [g.id, cleanQuantities(q[g.id] ?? {})])),
  };
}

// The buyer's own garment as the preview and size table read a color: the silhouette for its type, filled with the
// picked color, at the canvas and spots the server renders on (src/lib/own-garments.ts).
export function ownDesignGarment(o: OwnGarment): DesignGarment {
  const color = ownColor(o.color) ?? OWN_COLORS[0]!;
  const url = (side: "front" | "back") => `data:image/svg+xml;charset=utf-8,${encodeURIComponent(silhouetteSvg(o.garmentType, side, color.hex))}`;
  return {
    id: OWN_ID, name: `Your own ${OWN_LABEL[o.garmentType]}`, style: null, color: color.name, tone: color.tone, ppi: OWN_PPI,
    spots: OWN_SPOTS[o.garmentType], photoUrl: url("front"), backPhotoUrl: ownSides(o.garmentType).includes("back") ? url("back") : null,
    thumbUrl: url("front"), sizes: OWN_SIZES[o.garmentType],
  };
}

// The two unit prices under a size: the estimate's (with the prints, at the order's tier), or null until one is in.
export function shownUnit(g: DesignGarment, size: string, e: Estimate | null): Dual | null {
  return e?.units[g.id]?.[size] ?? null;
}

// The prints' tiers for what is placed ("Front center, medium" with Bank and Card), worked out on the page from the
// catalog's tiers by the server's box rule, so they show while the estimate loads. A print no tier holds (the sliders
// stop short of that) is left out; the server refuses it.
export function printLines(placements: SizedPlacement[], tiers: CatalogTier[]): PrintTierPair[] {
  return placements.flatMap((p) => {
    const t = tierForBox(p.widthIn, p.heightIn, tiers);
    return t ? [{ label: printTierLabel(p.spot, t.name), bankCents: t.bankCents, cardCents: t.cardCents }] : [];
  });
}

export function breakHint(addForBreak: number | null): string | null {
  return addForBreak ? `Add ${addForBreak} more for a lower price per garment.` : null;
}

// The buyer's details as both request bodies send them, trimmed.
export function contactBody(c: Contact): { contact: { name: string; email: string; phone: string; business: string }; note: string } {
  return { contact: { name: c.name.trim(), email: c.email.trim(), phone: c.phone.trim(), business: c.business.trim() }, note: c.note.trim() };
}

// The body Send posts. It never carries a price: the server prices from the cost.
export function designRequestBody(o: {
  requestId: string;
  logoId: string;
  garments: DesignGarment[];
  ink: Ink;
  // A moved logo carries offsetXIn and offsetYIn (offsetFields leaves them out at the standard position).
  placements: ({ spot: Spot; widthIn: number } & Partial<Offset>)[];
  quantities: Quantities;
  contact: Contact;
  website: string;
  own?: OwnGarment | null;
}): Record<string, unknown> {
  return {
    action: "request", requestId: o.requestId, logoId: o.logoId, ...(o.own ? { product: "own", own: o.own } : { garmentIds: o.garments.map((g) => g.id) }),
    ink: o.ink, placements: o.placements,
    quantities: Object.fromEntries(o.garments.map((g) => [g.id, cleanQuantities(o.quantities[g.id] ?? {})])),
    ...contactBody(o.contact), website: o.website,
  };
}

// ---- DTF transfers ----

export type Product = "apparel" | "transfers" | "own";

// The film: printerMaxWidthIn wide, up to maxLengthIn long (a gang sheet is exactly that).
export interface FilmRules {
  printerMaxWidthIn: number;
  maxLengthIn: number;
}

// The server's answer to a transfer estimate.
export interface TransferEstimate {
  quantity: number;
  total: Dual;
  unit: Dual;
  addForBreak: number | null;
  discount: Discount | null;
  shippingIncluded: boolean;
  size: { widthIn: number; heightIn: number };
  dpi: number;
}

export interface TransferPick {
  logoId: string;
  kind: TransferKind;
  widthIn: number;
  quantity: number;
  fulfillment: "pickup" | "ship";
}

const filmLimits = (r: FilmRules) => ({ maxWidthIn: r.printerMaxWidthIn, maxHeightIn: r.maxLengthIn });

// A piece's slider range: as wide as the film allows and no longer than the printer runs.
export function pieceBounds(logo: { widthPx: number; heightPx: number }, rules: FilmRules): { min: number; max: number } {
  return sliderBounds(filmLimits(rules), logo.heightPx / logo.widthPx, rules.printerMaxWidthIn);
}

// A piece's printed size at a width (the server sizes it the same way); a gang sheet is the whole film.
export function transferSize(kind: TransferKind, logo: { widthPx: number; heightPx: number }, widthIn: number, rules: FilmRules): { widthIn: number; heightIn: number } {
  if (kind === "gang") return { widthIn: rules.printerMaxWidthIn, heightIn: rules.maxLengthIn };
  const fit = fitPrintSize(logo.heightPx / logo.widthPx, widthIn, filmLimits(rules), rules.printerMaxWidthIn);
  return { widthIn: fit.widthIn, heightIn: fit.heightIn };
}

// The DPI a transfer prints at, judged as the server judges it: a gang sheet by its tighter side.
export function transferDpi(kind: TransferKind, logo: { widthPx: number; heightPx: number }, size: { widthIn: number; heightIn: number }): number {
  const across = effectiveDpi(logo.widthPx, size.widthIn);
  return kind === "gang" ? Math.min(across, effectiveDpi(logo.heightPx, size.heightIn)) : across;
}

// A transfer prints exactly as uploaded: under 150 DPI it would come out blurry, so the page blocks it (the server
// refuses it too); under 300 it is a warning.
export function transferDpiNotice(dpi: number, kind: TransferKind): { cls: "good" | "warn" | "bad"; text: string; blocked: boolean } {
  if (dpi < TRANSFER_LIMITS.minDpi) {
    return { cls: "bad", blocked: true, text: `This artwork is too small to print at this size (${dpi} DPI). Upload a larger file${kind === "piece" ? " or pick a smaller size" : ""}.` };
  }
  if (dpi < TRANSFER_LIMITS.sharpDpi) return { cls: "warn", blocked: false, text: `This will print a little soft at this size (${dpi} DPI). A larger file would be sharper.` };
  return { cls: "good", blocked: false, text: `Sharp at this size (${dpi} DPI).` };
}

// Null when a gang sheet is in the film's shape (2 by 3 within 2%), else what to fix.
export function gangMessage(logo: { widthPx: number; heightPx: number }, rules: FilmRules): string | null {
  if (gangRatioOk(logo.widthPx, logo.heightPx, rules.printerMaxWidthIn, rules.maxLengthIn)) return null;
  return `A gang sheet must be laid out ${rules.printerMaxWidthIn} in wide by ${rules.maxLengthIn} in tall. Yours is ${logo.widthPx} x ${logo.heightPx} px. Fix the layout and upload it again.`;
}

export function transferQuantityOk(kind: TransferKind, quantity: number): boolean {
  const max = kind === "gang" ? TRANSFER_LIMITS.maxGangSheets : TRANSFER_LIMITS.maxPieces;
  return Number.isInteger(quantity) && quantity >= 1 && quantity <= max;
}

export function transferBreakHint(addForBreak: number | null, kind: TransferKind): string | null {
  return addForBreak ? `Add ${addForBreak} more for a lower price per ${kind === "gang" ? "sheet" : "transfer"}.` : null;
}

// A gang sheet sends no width: it is the whole film.
const transferField = (t: TransferPick) => (t.kind === "gang" ? { kind: "gang", quantity: t.quantity } : { kind: "piece", widthIn: t.widthIn, quantity: t.quantity });

export function transferEstimateBody(t: TransferPick): Record<string, unknown> {
  return { action: "estimate", product: "transfers", logoId: t.logoId, transfer: transferField(t), fulfillment: t.fulfillment };
}

// The body Send posts for transfers: transfer fields only (the server refuses a mix), and never a price.
export function transferRequestBody(t: TransferPick & { requestId: string; contact: Contact; website: string }): Record<string, unknown> {
  return {
    action: "request", product: "transfers", requestId: t.requestId, logoId: t.logoId, transfer: transferField(t), fulfillment: t.fulfillment,
    ...contactBody(t.contact), website: t.website,
  };
}
